<!-- warehouse_scan/public/js/components/QtyDialog.vue -->
<template>
  <div class="dialog-overlay" @click.self="handleCancel">
    <div class="dialog-content">
      <div class="dialog-header">
        <div class="dialog-title">输入数量</div>
        <button class="close-btn" @click="handleCancel">✕</button>
      </div>
      
      <div class="dialog-body">
        <div class="info-row">
          <span class="label">物料</span>
          <span class="value code">{{ item?.item_code }}</span>
        </div>
        
        <div v-if="item?.current_qty !== undefined" class="info-row">
          <span class="label">当前库存</span>
          <span class="value highlight">{{ item.current_qty }}</span>
        </div>
        
        <div v-if="maxQty" class="info-row">
          <span class="label">最大数量</span>
          <span class="value highlight">{{ maxQty }}</span>
        </div>
        
        <!-- 批次号选择（盘点模式下有批次的物料） -->
        <div v-if="showBatchSelect" class="input-group batch-group">
          <label>批次号</label>
          <select v-if="item?.available_batches?.length" v-model="selectedBatch" class="batch-select">
            <option v-for="b in item.available_batches" :key="b" :value="b">{{ b }}</option>
          </select>
          <input v-else v-model="selectedBatch" type="text" class="batch-input" placeholder="请输入批次号" />
        </div>
        
        <div class="input-group">
          <label for="qtyInput">数量</label>
          <div class="qty-input-wrapper">
            <button class="qty-btn" @click="decrement" :disabled="allowZero ? qty <= 0 : qty <= 1">-</button>
            <input
              id="qtyInput"
              ref="inputRef"
              type="number"
              v-model.number="qty"
              :min="allowZero ? 0 : 1"
              :max="maxQty"
              step="1"
              class="qty-input"
              @keyup.enter="handleConfirm"
            >
            <button class="qty-btn" @click="increment" :disabled="maxQty && qty >= maxQty">+</button>
          </div>
        </div>
        
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
      </div>
      
      <div class="dialog-footer">
        <button class="dialog-btn cancel" @click="handleCancel">取消</button>
        <button class="dialog-btn confirm" @click="handleConfirm">确认</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  maxQty: {
    type: Number,
    default: null
  },
  allowZero: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['confirm', 'cancel']);

const qty = ref(1);
const error = ref('');
const inputRef = ref(null);
const selectedBatch = ref('');

// 是否显示批次选择
const showBatchSelect = computed(() => {
  return props.item?.has_batch_no && !props.item?.batch_no;
});

// 初始化批次号
watch(() => props.item, (newItem) => {
  if (newItem?.batch_no) {
    selectedBatch.value = newItem.batch_no;
  } else if (newItem?.available_batches?.length) {
    selectedBatch.value = newItem.available_batches[0];
  } else {
    selectedBatch.value = '';
  }
}, { immediate: true });

const increment = () => {
  if (!props.maxQty || qty.value < props.maxQty) {
    qty.value++;
    error.value = '';
  }
};

const decrement = () => {
  const minVal = props.allowZero ? 0 : 1;
  if (qty.value > minVal) {
    qty.value--;
    error.value = '';
  }
};

const handleConfirm = () => {
  const value = Number(qty.value);
  
  if (isNaN(value) || value < 0) {
    error.value = '请输入有效的数量';
    return;
  }
  
  if (!props.allowZero && value <= 0) {
    error.value = '数量必须大于0';
    return;
  }
  
  if (props.maxQty && value > props.maxQty) {
    error.value = `数量不能大于 ${props.maxQty}`;
    return;
  }
  
  // 盘点模式下有批次管理但未选择批次
  if (showBatchSelect.value && !selectedBatch.value) {
    error.value = '请选择或输入批次号';
    return;
  }
  
  emit('confirm', value, selectedBatch.value);
};

const handleCancel = () => {
  emit('cancel');
};

// 监听maxQty变化，调整当前值
watch(() => props.maxQty, (newMax) => {
  if (newMax && qty.value > newMax) {
    qty.value = newMax;
  }
});

onMounted(() => {
  nextTick(() => {
    if (inputRef.value) {
      inputRef.value.focus();
      inputRef.value.select();
    }
  });
});
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(3px);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.dialog-content {
  background: white;
  width: 90%;
  max-width: 350px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #f1f3f5;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-btn:hover {
  background: #e9ecef;
}

.dialog-body {
  padding: 20px;
}

.info-row {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
}

.info-row .label {
  width: 80px;
  color: #6c757d;
  font-size: 14px;
}

.info-row .value {
  flex: 1;
  font-weight: 500;
  color: #2c3e50;
}

.info-row .value.code {
  font-family: monospace;
  color: #007bff;
}

.info-row .value.highlight {
  color: #28a745;
  font-weight: 600;
}

.input-group {
  margin-top: 20px;
}

.input-group label {
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.batch-group {
  margin-top: 15px;
}

.batch-select, .batch-input {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 2px solid #dee2e6;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.3s;
  background: white;
}

.batch-select:focus, .batch-input:focus {
  border-color: #007bff;
}

.qty-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qty-btn {
  width: 44px;
  height: 44px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  font-size: 20px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.qty-btn:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #007bff;
}

.qty-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qty-input {
  flex: 1;
  padding: 12px;
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  outline: none;
  transition: all 0.3s;
}

.qty-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0,123,255,0.1);
}

.error-message {
  margin-top: 15px;
  padding: 10px;
  background: #f8d7da;
  border-radius: 8px;
  color: #721c24;
  font-size: 13px;
  text-align: center;
}

.dialog-footer {
  display: flex;
  gap: 10px;
  padding: 15px 20px 20px;
  border-top: 1px solid #f1f3f5;
}

.dialog-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.dialog-btn.confirm {
  background: #007bff;
  color: white;
}

.dialog-btn.confirm:hover {
  background: #0056b3;
}

.dialog-btn.cancel {
  background: #f1f3f5;
  color: #495057;
}

.dialog-btn.cancel:hover {
  background: #e9ecef;
}
</style>
